import {DepositoryType} from '@transactions/graphql';
import {
  Banknote,
  CreditCard,
  HandCoins,
  Landmark,
  Scale,
  TrendingUp,
  Wallet,
  WalletCards,
} from 'lucide-react';
import * as React from 'react';
import {cn} from 'src/lib/utils';
import type {LucideIcon} from 'lucide-react';

/**
 * Minimal account shape needed to pick an icon. Structurally compatible with
 * both AccountListItemFragment and InvestmentAccountFragmentFragment.
 */
type AccountForIcon =
  | {__typename: 'InvestmentAccount'}
  | {__typename: 'DepositoryAccount'; depositoryType: DepositoryType};

type Props = {
  account: AccountForIcon;
  className?: string;
};

const styleForAccount = (
  account: AccountForIcon,
): {Icon: LucideIcon; background: string} => {
  if (account.__typename === 'InvestmentAccount') {
    return {Icon: TrendingUp, background: 'bg-orange-600'};
  }

  switch (account.depositoryType) {
    case DepositoryType.Checking:
    case DepositoryType.Savings:
      return {Icon: Landmark, background: 'bg-blue-600'};
    case DepositoryType.CreditCard:
      return {Icon: CreditCard, background: 'bg-violet-600'};
    case DepositoryType.Cash:
      return {Icon: Banknote, background: 'bg-emerald-600'};
    case DepositoryType.Wallet:
      return {Icon: WalletCards, background: 'bg-teal-600'};
    case DepositoryType.Loan:
      return {Icon: HandCoins, background: 'bg-rose-600'};
    case DepositoryType.Ledger:
      // Clearing ledgers settle back to zero — the scale reads "balancing
      // out", and cyan keeps it distinct from every institution color.
      return {Icon: Scale, background: 'bg-cyan-600'};
    case DepositoryType.Other:
    default:
      return {Icon: Wallet, background: 'bg-slate-500'};
  }
};

/**
 * Default icon for accounts without a logo: a colored rounded-square badge
 * with a white glyph, matching the visual weight of account logo images.
 * Size it the same way as a logo `<img>` (e.g. `size-4`, `size-6`) via
 * className; defaults to `size-4`.
 */
export const AccountV2Icon = ({account, className}: Props) => {
  const {Icon, background} = styleForAccount(account);
  return (
    <div
      className={cn(
        'flex aspect-square size-4 items-center justify-center rounded-[25%] text-white',
        background,
        className,
      )}
    >
      <Icon className="size-[70%]" />
    </div>
  );
};
